import * as React from "react";
import PropTypes from "prop-types";
import { styled, css } from "@mui/system";
import {
  Modal as BaseModal,
  FormHelperText,
  Stack,
  TextField,
  Box,
  Typography,
  Divider,
  Avatar,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import { Button } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch } from "react-redux";
import moment from "moment";
import SafePayModal from "./SafePayModal";
import PaymentIcon from "@mui/icons-material/Payment";
import UploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

// 👉 APIs (add your real ones here)
import { UpdateElectricBill } from "../../../redux/reducer/UpdateElectricReading";
import { SsgcBill } from "../../../redux/reducer/SsgcReading";
import { MaintananceReadings } from "../../../redux/reducer/MaintananceReading";
import { WaterReadings } from "../../../redux/reducer/WaterReading";
import { TrashReadings } from "../../../redux/reducer/TrashReading";
import { GetTenantCurrentBill } from "../../../redux/reducer/GetTenantCurrentBill";

export default function TransitionsModal({
  modalValue,
  setIsModalVisible,
  ssgcModal,
  setSsgcModal,
  waterModal,
  setWaterModal,
  maintainanceModal,
  setMaintainanceModal,
  trashModal,
  setTrashModal,
  landlordId,
  flatId,
  selectedBillData,
  selectedBillType,
}) {
  const dispatch = useDispatch();
  const getId = window.location.pathname.replace("/current-Bill/", "");
  const currentDate = moment().format("DD-MM-YYYY");

  const [billData, setBillData] = React.useState([]);
  const [inputs, setInputs] = React.useState({});
  const [amountError, setAmountError] = React.useState("");
  const [billImage, setBillImage] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // SafePay Modal States
  const [safePayModal, setSafePayModal] = React.useState(false);
  const [currentBillType, setCurrentBillType] = React.useState("");
  const [currentAmount, setCurrentAmount] = React.useState(0);
  const [currentDueDate, setCurrentDueDate] = React.useState("");

  // ── کوئی بھی ایک modal کھلا ہے؟ ──
  const isAnyModalOpen =
    modalValue || ssgcModal || waterModal || maintainanceModal || trashModal;

  React.useEffect(() => {
    dispatch(GetTenantCurrentBill({ id: getId })).then((res) => {
      setBillData(res?.payload?.data?.data);
    });
  }, [dispatch, getId]);

  // ── FIX: isAnyModalOpen بھی dependency میں شامل کیا ──
  // جب بھی کوئی modal کھلے اور selectedBillData موجود ہو، amount set کرو
  React.useEffect(() => {
    if (isAnyModalOpen && selectedBillData?.billAmount) {
      setInputs((prev) => ({ ...prev, tenantAmount: selectedBillData.billAmount }));
      setAmountError("");
    }
  }, [isAnyModalOpen, selectedBillData]);

  /* ===================== HELPERS ===================== */

  const resetForm = () => {
    setInputs({});
    setAmountError("");
    setBillImage(null);
    setImagePreview(null);
    setIsSubmitting(false);
  };

  const closeAllModals = () => {
    setIsModalVisible(false);
    setSsgcModal(false);
    setWaterModal(false);
    setMaintainanceModal(false);
    setTrashModal(false);
    resetForm();
  };

  const getMaxAmount = () => selectedBillData?.billAmount || Infinity;

  const handleInputs = (e) => {
    const { value, name } = e.target;

    if (!value) {
      setAmountError("Amount is required");
    } else if (Number(value) <= 0) {
      setAmountError("Amount must be greater than 0");
    } else if (Number(value) > getMaxAmount()) {
      setAmountError(`Amount cannot exceed ${getMaxAmount()}`);
    } else {
      setAmountError("");
    }

    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setBillImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const openSafePayModal = (billType, amount, dueDate) => {
    setCurrentBillType(billType);
    setCurrentAmount(amount);
    setCurrentDueDate(dueDate);
    setSafePayModal(true);
  };

  const closeSafePayModal = () => {
    setSafePayModal(false);
    setCurrentBillType("");
    setCurrentAmount(0);
    setCurrentDueDate("");
  };

  /* ===================== MANUAL SUBMIT ===================== */

  const handleManualSubmit = async () => {
    if (!inputs?.tenantAmount || amountError) {
      toast.error("Please enter a valid amount");
      return;
    }

    const formData = new FormData();
    formData.append("tenantMessage", inputs?.tenantMessage || "");
    formData.append("tenantAmount", inputs?.tenantAmount);
    formData.append("upload_date", currentDate);
    formData.append("id", selectedBillData?._id);
    if (billImage) formData.append("bill_image", billImage);

    setIsSubmitting(true);

    try {
      let apiCall;

      switch (selectedBillType) {
        case "kelectric":
          apiCall = UpdateElectricBill;
          break;
        case "ssgc":
          apiCall = SsgcBill;
          break;
        case "water":
          apiCall = WaterReadings;
          break;
        case "maintainance":
          apiCall = MaintananceReadings;
          break;
        case "trash":
          apiCall = TrashReadings;
          break;
        default:
          throw new Error("Invalid bill type");
      }

      const res = await dispatch(apiCall({ id: getId, formData })).unwrap();

      toast.success(res?.message || "Bill submitted successfully!");
      closeAllModals();
    } catch (error) {
      toast.error(
        error?.message ||
        error?.response?.data?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===================== MODAL UI ===================== */

  const renderCommonModal = (open, onClose, title) => (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      closeAfterTransition
      slots={{ backdrop: StyledBackdrop }}
    >
      <Fade in={open}>
        <ModalContent sx={style}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            <IconButton onClick={() => { onClose(); resetForm(); }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Date: <strong>{currentDate}</strong>
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Bill Info Card */}
          <Box sx={{ background: "#f9fafb", borderRadius: 2, p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Due Date
                </Typography>
                <Typography fontWeight={600}>
                  {selectedBillData?.dueDate || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Bill
                </Typography>
                <Typography fontWeight={600}>
                  {selectedBillData?.billAmount || 0}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Form Section */}
          <Stack spacing={2}>
            <TextField
              label="Message (optional)"
              fullWidth
              size="small"
              name="tenantMessage"
              value={inputs.tenantMessage || ""}
              onChange={handleInputs}
            />

            <TextField
              label="Amount"
              type="number"
              fullWidth
              size="small"
              name="tenantAmount"
              value={inputs.tenantAmount || ""}
              disabled
              InputProps={{
                readOnly: true,
                sx: {
                  background: "#f3f4f6",
                  color: "#374151",
                  fontWeight: 600,
                  cursor: "not-allowed",
                },
              }}
              helperText="Amount is fixed as per total bill"
            />

            {/* Image Upload */}
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Upload Bill Image
                  <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                </Button>

                {imagePreview && (
                  <Avatar
                    variant="rounded"
                    src={imagePreview}
                    sx={{ width: 64, height: 64, borderRadius: 2 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{ background: "#000", "&:hover": { background: "#222" } }}
              disabled={
                !inputs?.tenantAmount ||
                !!amountError ||
                isSubmitting ||
                !billImage
              }
              onClick={handleManualSubmit}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? "Submitting..." : "Submit (Manual)"}
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<PaymentIcon />}
              sx={{
                background: "#00C853",
                "&:hover": { background: "#00A843" },
              }}
              onClick={() =>
                openSafePayModal(
                  selectedBillType,
                  selectedBillData?.billAmount || inputs?.tenantAmount,
                  selectedBillData?.dueDate
                )
              }
            >
              Pay with SafePay
            </Button>
          </Box>
        </ModalContent>
      </Fade>
    </Modal>
  );

  return (
    <>
      {renderCommonModal(modalValue, () => setIsModalVisible(false), "Pay Electricity Bill")}
      {renderCommonModal(ssgcModal, () => setSsgcModal(false), "Pay SSGC Bill")}
      {renderCommonModal(waterModal, () => setWaterModal(false), "Pay Water Bill")}
      {renderCommonModal(maintainanceModal, () => setMaintainanceModal(false), "Pay Maintenance Bill")}
      {renderCommonModal(trashModal, () => setTrashModal(false), "Pay Trash Bill")}

      <ToastContainer />

      {/* SafePay Modal */}
      <SafePayModal
        open={safePayModal}
        onClose={closeSafePayModal}
        billType={currentBillType}
        amount={currentAmount}
        billData={billData}
        tenantId={localStorage.getItem("user_id")}
        landlordId={landlordId}
        flatId={flatId}
        dueDate={currentDueDate}
      />
    </>
  );
}

/* ================== STYLES ================== */

const Backdrop = React.forwardRef((props, ref) => {
  const { open, ...other } = props;
  return (
    <Fade in={open}>
      <div ref={ref} {...other} />
    </Fade>
  );
});

Backdrop.propTypes = {
  open: PropTypes.bool,
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxWidth: "95%",
};

const ModalContent = styled("div")(
  ({ theme }) => css`
    font-family: "IBM Plex Sans", sans-serif;
    font-weight: 500;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    background-color: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border-radius: 16px;
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0 12px 30px
      ${theme.palette.mode === "dark" ? "rgb(0 0 0 / 0.6)" : "rgb(0 0 0 / 0.2)"};
    padding: 24px;
    color: ${theme.palette.mode === "dark" ? grey[50] : grey[900]};
  `
);